import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service for Patients & Providers — Heallexa" },
      { name: "description", content: "The legal terms covering provider listings, patient accounts, subscriptions, acceptable use, disclaimers, and liability for the Heallexa healthcare directory." },
      { property: "og:title", content: "Terms of Service for Patients & Providers — Heallexa" },
      { property: "og:description", content: "Terms covering provider listings, patient accounts, subscriptions, acceptable use, and disclaimers for the Heallexa healthcare directory." },
      { property: "og:url", content: "https://heallexa.com/terms" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://heallexa.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background pb-1">
      <Navbar />
      <main className="pt-32 md:pt-36 pb-16 sm:pb-24 mx-auto max-w-[760px] px-6">
        <div className="mb-10 pb-8 border-b border-border">
          <p className="text-[12px] uppercase tracking-[0.15em] text-muted-foreground mb-3">Legal</p>
          <h1 className="text-[2.4rem] font-medium tracking-[-0.02em] leading-tight">Terms of Service</h1>
          <p className="mt-3 text-[14px] text-muted-foreground">Last updated: May 22, 2026</p>
        </div>

        <div className="card-surface-flat rounded-md p-4 mb-8 text-[14px] space-y-2">
          <p><strong>Emergency notice.</strong> Heallexa does not provide emergency medical services. If you are experiencing a medical emergency, call 911 immediately.</p>
          <p><strong>Medical disclaimer.</strong> Heallexa does not provide medical advice, diagnosis, or treatment. Listings are informational only — always confirm details directly with the provider and consult a qualified healthcare professional.</p>
        </div>

        <div className="prose prose-sm max-w-none space-y-10 text-[15px] leading-relaxed text-foreground/85 [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_li]:list-disc [&_section>p]:mb-3">
          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">1. Acceptance of terms</h2>
            <p>These Terms of Service ("Terms") govern your access to and use of https://heallexa.com (the "Service"), operated by Heallexa Inc. ("Heallexa," "we," "us"). By using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">2. Eligibility and accounts</h2>
            <p>You must be at least 18 years old, or the age of majority in your jurisdiction, to create an account. You are responsible for the accuracy of the information you provide and for all activity that occurs under your account. Keep your credentials confidential.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">3. Nature of the Service</h2>
            <p>Heallexa is an online directory that helps patients discover and connect with healthcare providers. Heallexa is not a healthcare provider, does not practice medicine, and does not endorse, recommend, certify, or verify the qualifications of any listed provider. All listings are informational. You are solely responsible for your healthcare decisions.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">4. Provider listings, claims, and corrections</h2>
            <p>Provider listings may be created from public information. Providers may claim, edit, and update their listing after identity verification. If you are a provider and want to correct, claim, or remove a listing, email <a className="underline" href="mailto:contact@heallexa.com">contact@heallexa.com</a> from an address associated with your practice. We will verify your identity and respond promptly. Misrepresentation may result in listing removal or account termination.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">5. User content</h2>
            <p>You retain ownership of content you submit (such as reviews, photos, or listing details), and you grant Heallexa a worldwide, non-exclusive, royalty-free license to host, display, reproduce, and distribute that content as necessary to operate and promote the Service. You represent that your content is accurate, lawful, and does not infringe any third party's rights.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">6. Acceptable use</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>No unlawful, fraudulent, defamatory, or harassing activity.</li>
              <li>No scraping, bulk data extraction, or automated access without prior written consent.</li>
              <li>No attempts to interfere with the Service, bypass security, or access another user's account.</li>
              <li>No misrepresentation of identity, credentials, or affiliation.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">7. Subscriptions and paid plans</h2>
            <p>Some features may be offered through paid subscriptions or one-time purchases. Fees are billed in advance and are non-refundable except where required by law. You may cancel at any time; cancellation takes effect at the end of the current billing period.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">8. Intellectual property</h2>
            <p>The Service, including its software, design, and branding, is owned by Heallexa Inc. and protected by intellectual property laws. We grant you a limited, revocable, non-transferable license to use the Service for its intended purpose.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">9. Termination</h2>
            <p>We may suspend or terminate your access at any time for any reason, including violation of these Terms. You may stop using the Service at any time. Sections that by their nature should survive termination will survive.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">10. Disclaimers</h2>
            <p>The Service is provided "as is" and "as available" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that listings are complete, accurate, or current.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">11. Limitation of liability</h2>
            <p>To the maximum extent permitted by law, Heallexa Inc. and its affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or goodwill, arising out of or related to your use of the Service. Our total liability for any claim related to the Service will not exceed the greater of the amount you paid Heallexa in the twelve months before the claim, or US $100.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">12. Indemnification</h2>
            <p>You agree to indemnify and hold harmless Heallexa Inc., its officers, directors, employees, and agents from any claims, damages, liabilities, and expenses arising from your use of the Service, your content, or your violation of these Terms.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">13. Governing law and disputes</h2>
            <p>These Terms are governed by the laws of the state where Heallexa Inc. is organized, without regard to its conflict-of-laws rules. Any dispute will be resolved in the courts located in that state, and you consent to their exclusive jurisdiction.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">14. Changes to the Terms</h2>
            <p>We may update these Terms from time to time. When we do, we will revise the effective date above and, if changes are material, provide additional notice. Continued use of the Service after changes take effect constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-[17px] font-medium text-foreground mb-3 mt-8">15. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">If you have any questions, concerns, or feedback regarding these Terms, please contact us:</p>
            <div className="mt-4 rounded-xl border border-border/80 bg-muted/20 p-6 sm:p-7 text-[14.5px] leading-relaxed text-foreground space-y-2">
              <p>Heallexa Inc.</p>
              <p>2351 W Northwest Hwy, Suite 1234, Dallas, TX 75220</p>
              <p>Email: <a className="text-primary hover:underline" href="mailto:contact@heallexa.com">contact@heallexa.com</a></p>
              <p>Website: <a className="text-primary hover:underline" href="https://heallexa.com">heallexa.com</a></p>
            </div>
          </section>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
